import type { Worker } from 'tesseract.js'

/**
 * In-browser OCR for the Labels page, self-hosted so nothing leaves the
 * portal's origin — worker script, wasm core and English traineddata are
 * all served from /ocr (copied there at build time, see vite.config.ts) and
 * `workerBlobURL: false` keeps the worker itself same-origin rather than a
 * blob: URL, which is what lets the CSP get away with `worker-src 'self'`.
 *
 * tesseract.js is only ever reached through the dynamic import below, so it
 * (and the ~5MB wasm core it pulls in) stays out of the app's main bundle
 * until a staff member actually opens the photo-capture flow.
 */

let workerPromise: Promise<Worker> | null = null

async function getWorker(onProgress?: (fraction: number) => void): Promise<Worker> {
  if (!workerPromise) {
    const { createWorker } = await import('tesseract.js')
    workerPromise = createWorker('eng', 1, {
      workerPath: '/ocr/worker.min.js',
      corePath: '/ocr/core/',
      langPath: '/ocr/lang',
      workerBlobURL: false,
      logger: (m) => {
        if (m.status === 'recognizing text') onProgress?.(m.progress)
      },
    })
  }
  return workerPromise
}

/**
 * Recognizes text in a photographed label. `digitsOnly` narrows the
 * character whitelist for IMEI/ICCID capture — the LSTM engine only honors
 * a whitelist loosely, so this helps accuracy but callers must not depend on
 * it for correctness: candidate extraction downstream strips non-digits
 * regardless, and the value is always confirmed by a human before use.
 */
export async function recognize(
  image: File,
  opts: { digitsOnly: boolean; onProgress?: (fraction: number) => void },
): Promise<string> {
  const worker = await getWorker(opts.onProgress)
  await worker.setParameters({
    tessedit_char_whitelist: opts.digitsOnly ? '0123456789' : '',
  })
  const {
    data: { text },
  } = await worker.recognize(image)
  return text
}

/** Releases the OCR worker. Call on unmount of the page that uses it — an
 *  idle worker holds onto a wasm instance that is otherwise never freed. */
export async function terminateOcr(): Promise<void> {
  if (!workerPromise) return
  const worker = await workerPromise
  workerPromise = null
  await worker.terminate()
}
